import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../css/BulkEmail.css";
import AdminHeader from "../../components/header/AdminHeader";
import { toast } from "react-hot-toast";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import PreviewIcon from "@mui/icons-material/Visibility";
import PeopleIcon from "@mui/icons-material/People";
import SubjectIcon from "@mui/icons-material/Subject";
import MessageIcon from "@mui/icons-material/Message";

const apiUrl = `${process.env.REACT_APP_API}`;

function BulkEmail() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [recipientCount, setRecipientCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    getRecipientCount();
  }, []);

  const getRecipientCount = async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/all-users`);
      setRecipientCount(data.data || 0);
    } catch (err) {
      console.error("Error fetching user count:", err);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!subject.trim()) {
      errors.subject = "Subject is required";
    } else if (subject.length < 3) {
      errors.subject = "Subject must be at least 3 characters";
    } else if (subject.length > 100) {
      errors.subject = "Subject must be less than 100 characters";
    }

    if (!message.trim()) {
      errors.message = "Message is required";
    } else if (message.length < 10) {
      errors.message = "Message must be at least 10 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before sending");
      return;
    }

    const confirmSend = window.confirm(
        `Are you sure you want to send this email to ${recipientCount} customers?`
    );

    if (!confirmSend) return;

    setIsSending(true);

    try {
      await axios.post(`${apiUrl}/send`, {
        subject: subject,
        message: message,
      });

      toast.success(`Email sent successfully to ${recipientCount} customers! 🎉`);

      // Clear form
      setSubject("");
      setMessage("");
      setFormErrors({});
    } catch (err) {
      console.error("Error sending email:", err);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handlePreview = () => {
    if (!subject.trim() && !message.trim()) {
      toast.error("Please enter subject and message to preview");
      return;
    }
    setShowPreview(!showPreview);
  };

  const insertTemplate = (template) => {
    switch (template) {
      case "newArrival":
        setSubject("✨ New Arrivals Just For You!");
        setMessage(
            "Dear Valued Customer,\n\n" +
            "We're excited to announce our latest collection of traditional Indian clothing! " +
            "Discover stunning new designs that blend timeless elegance with modern style.\n\n" +
            "Shop now and enjoy exclusive early access to our newest arrivals.\n\n" +
            "Best regards,\n" +
            "Kanchan's Choice Team"
        );
        break;
      case "sale":
        setSubject("🎉 Special Sale - Up to 50% Off!");
        setMessage(
            "Dear Valued Customer,\n\n" +
            "Don't miss out on our biggest sale of the season! " +
            "Save up to 50% on selected items from our beautiful collection.\n\n" +
            "This exclusive offer is valid for a limited time only.\n\n" +
            "Shop now before it's too late!\n\n" +
            "Warm regards,\n" +
            "Kanchan's Choice Team"
        );
        break;
      case "thankyou":
        setSubject("💝 Thank You for Your Support!");
        setMessage(
            "Dear Valued Customer,\n\n" +
            "We wanted to take a moment to thank you for being a part of our journey. " +
            "Your continued support means the world to us!\n\n" +
            "As a token of our appreciation, enjoy a special discount on your next purchase.\n\n" +
            "With gratitude,\n" +
            "Kanchan's Choice Team"
        );
        break;
      default:
        break;
    }
    toast.success("Template loaded!");
  };

  return (
      <>
        <AdminHeader />
        <div className="bulk-email-wrapper">
          <div className="bulk-email-container">
            {/* Header */}
            <div className="email-header">
              <div className="header-icon">
                <EmailIcon />
              </div>
              <div>
                <h1 className="email-title">Bulk Email Campaign</h1>
                <p className="email-subtitle">
                  Send promotional emails to all your customers
                </p>
              </div>
            </div>

            {/* Recipient Info Card */}
            <div className="recipient-card">
              <div className="recipient-icon">
                <PeopleIcon />
              </div>
              <div className="recipient-info">
                <h3>Recipients</h3>
                <p className="recipient-count">{recipientCount} customers</p>
                <p className="recipient-detail">
                  This email will be sent to all registered users
                </p>
              </div>
            </div>

            {/* Quick Templates */}
            <div className="templates-section">
              <h3 className="templates-title">Quick Templates</h3>
              <div className="templates-grid">
                <button
                    type="button"
                    className="template-btn"
                    onClick={() => insertTemplate("newArrival")}
                >
                  <span className="template-emoji">✨</span>
                  <span>New Arrivals</span>
                </button>
                <button
                    type="button"
                    className="template-btn"
                    onClick={() => insertTemplate("sale")}
                >
                  <span className="template-emoji">🎉</span>
                  <span>Sale Announcement</span>
                </button>
                <button
                    type="button"
                    className="template-btn"
                    onClick={() => insertTemplate("thankyou")}
                >
                  <span className="template-emoji">💝</span>
                  <span>Thank You</span>
                </button>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleSubmit} className="email-form">
              <div className="form-card">
                {/* Subject Field */}
                <div className="form-group">
                  <label htmlFor="subject" className="form-label">
                    <SubjectIcon /> Email Subject *
                  </label>
                  <input
                      type="text"
                      id="subject"
                      className={`form-input ${formErrors.subject ? 'error' : ''}`}
                      placeholder="Enter email subject..."
                      value={subject}
                      onChange={(e) => {
                        setSubject(e.target.value);
                        if (formErrors.subject) {
                          setFormErrors({ ...formErrors, subject: null });
                        }
                      }}
                      maxLength="100"
                  />
                  {formErrors.subject && (
                      <span className="error-message">{formErrors.subject}</span>
                  )}
                  <div className="char-count">
                    {subject.length}/100 characters
                  </div>
                </div>

                {/* Message Field */}
                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    <MessageIcon /> Email Message *
                  </label>
                  <textarea
                      id="message"
                      className={`form-textarea ${formErrors.message ? 'error' : ''}`}
                      placeholder="Write your message here..."
                      value={message}
                      onChange={(e) => {
                        setMessage(e.target.value);
                        if (formErrors.message) {
                          setFormErrors({ ...formErrors, message: null });
                        }
                      }}
                      rows="10"
                  ></textarea>
                  {formErrors.message && (
                      <span className="error-message">{formErrors.message}</span>
                  )}
                  <div className="char-count">
                    {message.length} characters
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="form-actions">
                  <button
                      type="button"
                      className="btn-preview"
                      onClick={handlePreview}
                      disabled={isSending}
                  >
                    <PreviewIcon /> Preview Email
                  </button>
                  <button
                      type="submit"
                      className="btn-send"
                      disabled={isSending}
                  >
                    {isSending ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sending...
                        </>
                    ) : (
                        <>
                          <SendIcon /> Send to {recipientCount} Customers
                        </>
                    )}
                  </button>
                </div>
              </div>
            </form>

            {/* Preview Modal */}
            {showPreview && (
                <div className="preview-modal" onClick={() => setShowPreview(false)}>
                  <div className="preview-content" onClick={(e) => e.stopPropagation()}>
                    <div className="preview-header">
                      <h3>Email Preview</h3>
                      <button
                          className="preview-close"
                          onClick={() => setShowPreview(false)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="preview-body">
                      <div className="preview-subject">
                        <strong>Subject:</strong> {subject || "(No subject)"}
                      </div>
                      <div className="preview-divider"></div>
                      <div className="preview-message">
                        {message || "(No message)"}
                      </div>
                    </div>
                    <div className="preview-footer">
                      <button
                          className="btn-close-preview"
                          onClick={() => setShowPreview(false)}
                      >
                        Close Preview
                      </button>
                    </div>
                  </div>
                </div>
            )}

            {/* Tips Section */}
            <div className="tips-card">
              <h3>📝 Email Tips</h3>
              <ul>
                <li>Keep your subject line clear and engaging</li>
                <li>Personalize your message when possible</li>
                <li>Include a clear call-to-action</li>
                <li>Preview your email before sending</li>
                <li>Test with a small group first if possible</li>
              </ul>
            </div>
          </div>
        </div>
      </>
  );
}

export default BulkEmail;